<template>
  <div class="products-page">
    <div class="page-header">
      <h1>📦 Billbee Products</h1>
      <div class="header-actions">
        <button @click="fetchProducts" :disabled="loading" class="btn btn-primary">
          <span v-if="loading">⏳</span>
          <span v-else>🔄</span>
          Refresh
        </button>
        <button @click="exportToExcel" :disabled="loading || !hasData" class="btn btn-success">
          <span>📥</span>
          Export XLSX
        </button>
      </div>
    </div>

    <!-- Category Filter -->
    <div v-if="categories.length > 0 || allCategories.length > 0" class="filter-section">
      <div class="filter-group">
        <label for="category-filter">Filter by Category:</label>
        <select id="category-filter" v-model="selectedCategoryKey" class="category-select">
          <option value="">All Products ({{ allProducts.length }})</option>
          <option v-for="cat in sortedCategories" :key="cat.key" :value="cat.key">
            {{ cat.name }} - ID: {{ cat.id || 'N/A' }}
          </option>
        </select>
      </div>
      <div v-if="selectedCategoryKey" class="active-filter">
        <span class="filter-badge">
          Filtered: {{ getCategoryName(selectedCategoryKey) }} ({{ filteredProducts.length }} products)
          <button @click="selectedCategoryKey = ''" class="clear-filter">✕</button>
        </span>
      </div>
    </div>

    <!-- Stats Cards -->
    <div v-if="hasData" class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{{ filteredProducts.length }}</div>
        <div class="stat-label">{{ selectedCategoryKey ? 'Filtered Products' : 'Total Products' }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ filteredBundleProducts.length }}</div>
        <div class="stat-label">Bundle Products</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ totalStock }}</div>
        <div class="stat-label">Total Stock</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ sortedCategories.length }}</div>
        <div class="stat-label">Categories</div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading products from Billbee...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <p>❌ {{ error }}</p>
      <button @click="fetchProducts" class="btn btn-primary">Retry</button>
    </div>

    <!-- Products Tabs -->
    <div v-else-if="hasData" class="content-section">
      <div class="tabs">
        <button 
          @click="activeTab = 'products'" 
          :class="['tab-btn', { active: activeTab === 'products' }]"
        >
          All Products ({{ filteredProducts.length }})
        </button>
        <button 
          @click="activeTab = 'bundles'" 
          :class="['tab-btn', { active: activeTab === 'bundles' }]"
        >
          Bundle Overview ({{ filteredBundleProducts.length }})
        </button>
        <button 
          @click="activeTab = 'categories'" 
          :class="['tab-btn', { active: activeTab === 'categories' }]"
        >
          Categories ({{ sortedCategories.length }})
        </button>
      </div>

      <!-- All Products Table -->
      <div v-if="activeTab === 'products'" class="table-container">
        <table class="products-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Stock</th>
              <th>Stock Desired</th>
              <th>Stock Warning</th>
              <th>Type</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in filteredProducts" :key="product.id">
              <td><code>{{ product.sku }}</code></td>
              <td>{{ product.title }}</td>
              <td :class="getStockClass(product)">{{ product.stock }}</td>
              <td>{{ product.stock_desired }}</td>
              <td>{{ product.stock_warning }}</td>
              <td>
                <span :class="['badge', product.is_bundle ? 'badge-bundle' : 'badge-single']">
                  {{ product.is_bundle ? '📦 Bundle' : '📄 Single' }}
                </span>
              </td>
              <td>
                <span v-if="getCategoryName(product.category1_id)" class="category-cell">
                  {{ getCategoryName(product.category1_id) }}
                  <small v-if="product.category1_id">(ID: {{ product.category1_id }})</small>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bundle Overview Table -->
      <div v-if="activeTab === 'bundles'" class="table-container">
        <div v-if="filteredBundleProducts.length === 0" class="empty-state">
          <p>No bundle products found{{ selectedCategoryKey ? ' in selected category' : '' }}.</p>
        </div>
        <div v-else class="bundles-list">
          <div v-for="bundle in filteredBundleProducts" :key="bundle.id" class="bundle-card">
            <div class="bundle-header">
              <h3>{{ bundle.title }}</h3>
              <div class="bundle-info">
                <span class="badge badge-bundle">Bundle</span>
                <span class="stock-badge" :class="getStockClass(bundle)">
                  Stock: {{ bundle.stock }}
                </span>
              </div>
            </div>
            
            <div class="bundle-components">
              <h4>Components:</h4>
              <table class="components-table">
                <thead>
                  <tr>
                    <th>Product ID</th>
                    <th>Product Name</th>
                    <th>Quantity Needed</th>
                    <th>Available Stock</th>
                    <th>Possible Bundles</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(component, idx) in getBundleComponents(bundle)" :key="idx">
                    <td><code>{{ component.productId }}</code></td>
                    <td>{{ component.name }}</td>
                    <td>{{ component.quantity }}</td>
                    <td :class="component.stockClass">{{ component.stock }}</td>
                    <td :class="component.stockClass">{{ component.possibleBundles }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="bundle-summary">
                <strong>Calculated Bundle Stock:</strong> 
                <span :class="['calculated-stock', getBundleStockClass(bundle)]">
                  {{ getCalculatedBundleStock(bundle) }}
                </span>
                <span class="stock-note">(based on limiting component)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Categories Overview -->
      <div v-if="activeTab === 'categories'" class="table-container">
        <div class="categories-info">
          <p class="info-text">
            Click on a category to filter products. Category IDs are shown for reference.
          </p>
        </div>
        <table class="products-table">
          <thead>
            <tr>
              <th>Category ID</th>
              <th>Category Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cat in sortedCategories" :key="cat.key">
              <td><code>{{ cat.id || 'N/A' }}</code></td>
              <td><strong>{{ cat.name }}</strong></td>
              <td>
                <button 
                  @click="selectedCategoryKey = cat.key; activeTab = 'products'" 
                  class="btn-small btn-primary"
                >
                  View Products
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <p>No products loaded. Click "Refresh" to fetch products.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import api from '@/utils/api'
import * as XLSX from 'xlsx'

const loading = ref(false)
const error = ref(null)
const allProducts = ref([])
const bundleProducts = ref([])
const categories = ref([])
const allCategories = ref([]) // All categories from Billbee
const rawProducts = ref([])
const activeTab = ref('products')
const selectedCategoryKey = ref('')
const lastFetchedAt = ref(null)

const hasData = computed(() => allProducts.value.length > 0)

// Sorted categories - use all categories if available, otherwise used categories
const sortedCategories = computed(() => {
  const categoriesToShow = allCategories.value.length > 0 ? allCategories.value : categories.value
  
  return categoriesToShow
    .map(c => ({
      id: c.Id || c.id,
      name: c.Name || c.name,
      key: String(c.Id || c.id || c.Name || c.name) // Convert to string for v-model
    }))
    .filter(c => c.id) // Only show categories with IDs
    .sort((a, b) => a.name.localeCompare(b.name))
})

// Filtered products based on selected category (client-side)
const filteredProducts = computed(() => {
  if (!selectedCategoryKey.value) {
    return allProducts.value
  }
  
  const categoryIdToFilter = Number(selectedCategoryKey.value)
  
  return allProducts.value.filter(p => {
    return p.category1_id === categoryIdToFilter ||
           p.category2_id === categoryIdToFilter ||
           p.category3_id === categoryIdToFilter
  })
})

const filteredBundleProducts = computed(() => {
  if (!selectedCategoryKey.value) {
    return bundleProducts.value
  }
  
  return bundleProducts.value.filter(p => {
    return filteredProducts.value.some(fp => fp.id === p.id)
  })
})

const totalStock = computed(() => {
  return filteredProducts.value.reduce((sum, p) => sum + (p.stock || 0), 0)
})

const lowStockCount = computed(() => {
  return filteredProducts.value.filter(p => 
    p.stock < p.stock_warning && p.stock_warning > 0
  ).length
})

// Get category name by key (ID or name)
function getCategoryName(categoryId) {
  if (!categoryId) return ''
  // categoryId could be a number or string, convert for comparison
  const idToFind = Number(categoryId)
  const cat = sortedCategories.value.find(c => Number(c.id) === idToFind)
  return cat ? cat.name : ''
}

// Fetch products from API
async function fetchProducts() {
  loading.value = true
  error.value = null
  
  try {
    const response = await api.get('/api/endpoints/products.php')
    
    if (response.data.success) {
      allProducts.value = response.data.data.products || []
      bundleProducts.value = response.data.data.bundle_products || []
      categories.value = response.data.data.categories || []
      allCategories.value = response.data.data.all_categories || []
      rawProducts.value = response.data.data.raw_products || []
      lastFetchedAt.value = response.data.meta.fetched_at
      
      console.log('Products loaded:', {
        total: allProducts.value.length,
        bundles: bundleProducts.value.length,
        categories: categories.value.length,
        allCategories: allCategories.value.length
      })
    } else {
      error.value = response.data.error || 'Failed to fetch products'
    }
  } catch (err) {
    console.error('Error fetching products:', err)
    error.value = err.message || 'Failed to fetch products'
  } finally {
    loading.value = false
  }
}

// Get stock class for styling
function getStockClass(product) {
  if (product.stock === 0) return 'stock-zero'
  if (product.stock_warning > 0 && product.stock < product.stock_warning) return 'stock-low'
  return 'stock-ok'
}

// Get bundle stock class
function getBundleStockClass(bundle) {
  const calculatedStock = getCalculatedBundleStock(bundle)
  if (calculatedStock === 0) return 'stock-zero'
  if (calculatedStock < 5) return 'stock-low'
  return 'stock-ok'
}

// Get bundle components with stock info
function getBundleComponents(bundle) {
  if (!bundle.bill_of_material || bundle.bill_of_material.length === 0) {
    return []
  }
  
  return bundle.bill_of_material.map(item => {
    // Find the child product by ArticleId or SKU
    const childProduct = rawProducts.value.find(p => 
      p.Id === item.ArticleId || p.SKU === item.SKU
    )
    
    const stock = childProduct ? (childProduct.StockCurrent || 0) : 0
    const quantity = item.Amount || 1
    const possibleBundles = quantity > 0 ? Math.floor(stock / quantity) : 0
    
    return {
      productId: item.ArticleId,
      name: childProduct ? extractTitle(childProduct) : `Product: ${item.SKU}`,
      sku: item.SKU || 'N/A',
      quantity: quantity,
      stock: stock,
      possibleBundles: possibleBundles,
      stockClass: stock === 0 ? 'stock-zero' : (possibleBundles < 5 ? 'stock-low' : 'stock-ok')
    }
  })
}

// Helper to extract title from product (handles multi-language structure)
function extractTitle(product) {
  if (!product) return ''
  const title = product.Title
  if (Array.isArray(title) && title.length > 0) {
    // Find German title first
    const germanTitle = title.find(t => t.LanguageCode === 'DE')
    if (germanTitle) return germanTitle.Text || ''
    // Otherwise return first title
    return title[0].Text || ''
  }
  return title || ''
}

// Calculate bundle stock based on limiting component
function getCalculatedBundleStock(bundle) {
  const components = getBundleComponents(bundle)
  
  if (components.length === 0) {
    return 0
  }
  
  // Find the minimum possible bundles across all components
  return Math.min(...components.map(c => c.possibleBundles))
}

// Export to Excel
function exportToExcel() {
  try {
    // Create a new workbook
    const wb = XLSX.utils.book_new()
    
    // Use filtered products for export
    const productsToExport = selectedCategoryKey.value ? filteredProducts.value : allProducts.value
    const bundlesToExport = selectedCategoryKey.value ? filteredBundleProducts.value : bundleProducts.value
    
    // Sheet 1: All Products (or filtered)
    const productsData = [
      ['SKU', 'Product Name', 'Stock', 'Stock Desired', 'Stock Warning', 'Type', 'Category 1', 'Category 2', 'Category 3']
    ]
    
    productsToExport.forEach(product => {
      productsData.push([
        product.sku,
        product.title,
        product.stock,
        product.stock_desired,
        product.stock_warning,
        product.is_bundle ? 'Bundle' : 'Single',
        product.category1 || '',
        product.category2 || '',
        product.category3 || ''
      ])
    })
    
    const ws1 = XLSX.utils.aoa_to_sheet(productsData)
    XLSX.utils.book_append_sheet(wb, ws1, 'Products')
    
    // Sheet 2: Bundle Overview
    const bundleData = [
      ['Bundle SKU', 'Bundle Name', 'Bundle Stock', 'Component SKU', 'Component Name', 'Quantity Needed', 'Available Stock', 'Possible Bundles', 'Calculated Bundle Stock']
    ]
    
    bundlesToExport.forEach(bundle => {
      const components = getBundleComponents(bundle)
      const calculatedStock = getCalculatedBundleStock(bundle)
      
      components.forEach((component, idx) => {
        bundleData.push([
          idx === 0 ? bundle.sku : '', // Only show bundle info on first row
          idx === 0 ? bundle.title : '',
          idx === 0 ? bundle.stock : '',
          component.sku,
          component.name,
          component.quantity,
          component.stock,
          component.possibleBundles,
          idx === 0 ? calculatedStock : ''
        ])
      })
      
      // Add empty row between bundles
      bundleData.push(['', '', '', '', '', '', '', '', ''])
    })
    
    const ws2 = XLSX.utils.aoa_to_sheet(bundleData)
    XLSX.utils.book_append_sheet(wb, ws2, 'Bundle Overview')
    
    // Generate filename with date
    const date = new Date().toISOString().split('T')[0]
    const categoryName = selectedCategoryKey.value ? '_' + getCategoryName(selectedCategoryKey.value).replace(/[^a-zA-Z0-9]/g, '-') : ''
    const filename = `Billbee_Products${categoryName}_${date}.xlsx`
    
    // Save file
    XLSX.writeFile(wb, filename)
    
  } catch (err) {
    console.error('Error exporting to Excel:', err)
    alert('Failed to export to Excel: ' + err.message)
  }
}

// Fetch products on mount
fetchProducts()
</script>

<style scoped>
.products-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  color: #2c3e50;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
}

.filter-section {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.filter-group label {
  font-weight: 600;
  color: #475569;
  min-width: 150px;
}

.category-select {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.category-select:focus {
  outline: none;
  border-color: #3b82f6;
}

.category-select:hover {
  border-color: #cbd5e1;
}

.active-filter {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 8px;
  font-weight: 500;
}

.clear-filter {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: #1e40af;
  padding: 0;
  margin-left: 0.5rem;
  transition: color 0.2s ease;
}

.clear-filter:hover {
  color: #ef4444;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3b82f6;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #64748b;
  font-size: 0.9rem;
}

.loading, .error-state, .empty-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state {
  color: #ef4444;
}

.content-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  background: #f8fafc;
}

.tab-btn {
  padding: 1rem 2rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  color: #64748b;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
}

.tab-btn:hover {
  background: #e5e7eb;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: white;
}

.table-container {
  overflow-x: auto;
  padding: 1.5rem;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
}

.products-table th,
.products-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.products-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
}

.products-table tbody tr:hover {
  background: #f8fafc;
}

.products-table code {
  background: #e5e7eb;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.category-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.category-cell small {
  color: #64748b;
  font-size: 0.75rem;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.badge-bundle {
  background: #dbeafe;
  color: #1e40af;
}

.badge-single {
  background: #e0e7ff;
  color: #4338ca;
}

.stock-zero {
  color: #ef4444;
  font-weight: bold;
}

.stock-low {
  color: #f59e0b;
  font-weight: bold;
}

.stock-ok {
  color: #10b981;
}

.bundles-list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.bundle-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  background: #fafafa;
}

.bundle-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.bundle-header h3 {
  margin: 0;
  color: #2c3e50;
}

.bundle-info {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.stock-badge {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  background: white;
}

.bundle-components h4 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: #475569;
}

.components-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background: white;
  border-radius: 6px;
  overflow: hidden;
}

.components-table th,
.components-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.components-table th {
  background: #f1f5f9;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
}

.components-table code {
  background: #e5e7eb;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
}

.bundle-summary {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 6px;
  border-left: 4px solid #3b82f6;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.calculated-stock {
  font-size: 1.25rem;
  font-weight: bold;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  background: #f1f5f9;
}

.stock-note {
  color: #64748b;
  font-size: 0.875rem;
  font-style: italic;
}

.btn-small {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-small.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-small.btn-primary:hover {
  background: #2563eb;
}

.categories-info {
  padding: 1rem;
  background: #f0f9ff;
  border-left: 4px solid #3b82f6;
  margin-bottom: 1rem;
  border-radius: 6px;
}

.info-text {
  margin: 0;
  color: #1e40af;
  font-size: 0.95rem;
}
</style>
